import axios from 'axios';
import { apiUrl } from './apiConfig';
import { setUser as persistUser } from './auth';

import logo from '../assets/logo.png';
import isoCube from '../assets/iso-cube-clear.png';
import isoPyramid from '../assets/iso-pyramid-clear.png';
import timeIcon from '../assets/time-icon-192.webp';
import bankSecure from '../assets/bank-secure-192.webp';

export const PREFETCH_KEYS = {
  groups: 'kkt_prefetch_groups',
  teachers: 'kkt_prefetch_teachers',
  bannersSchedule: 'kkt_prefetch_banners_schedule',
  bannersProfile: 'kkt_prefetch_banners_profile',
  theme: 'kkt_prefetch_theme',
};

const PUBLIC_BASE =
  (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';

const CRITICAL_IMAGES = [
  `${PUBLIC_BASE}/logo.png`,
  `${PUBLIC_BASE}/iso-cube-clear.png`,
  `${PUBLIC_BASE}/iso-pyramid-clear.png`,
];

const WARM_IMAGES = [logo, isoCube, isoPyramid, timeIcon, bankSecure];

function loadImage(src, { timeoutMs = 1500, priority = 'auto' } = {}) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(false);
      return;
    }
    const img = new Image();
    let settled = false;
    const done = (ok) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    const timer = window.setTimeout(() => done(false), timeoutMs);
    img.onload = () => {
      window.clearTimeout(timer);
      done(true);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      done(false);
    };
    try {
      if ('fetchPriority' in img) img.fetchPriority = priority;
    } catch {
      /* ignore */
    }
    img.decoding = 'async';
    img.src = src;
  });
}

function savePrefetch(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), value }));
  } catch {
    /* ignore quota */
  }
}

export function readPrefetch(key, maxAgeMs = 10 * 60 * 1000) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (Date.now() - (parsed.at || 0) > maxAgeMs) return null;
    return parsed.value;
  } catch {
    return null;
  }
}

async function warmJson(url, key, params) {
  try {
    const { data } = await axios.get(apiUrl(url), {
      params,
      timeout: 4000,
    });
    savePrefetch(key, data);
    return data;
  } catch {
    return null;
  }
}

async function warmBanners(placement, key) {
  try {
    const { data } = await axios.get(apiUrl('/api/public/banners'), {
      params: { placement },
      timeout: 3500,
    });
    savePrefetch(key, data);
    const urls = (data?.banners || [])
      .map((b) => b?.image_url || b?.image || b?.url)
      .filter(Boolean)
      .map((u) => (/^https?:\/\//i.test(u) ? u : apiUrl(u)));
    await Promise.all(urls.slice(0, 4).map((u) => loadImage(u, { timeoutMs: 2500 })));
    return data;
  } catch {
    return null;
  }
}

function warmInBackground(token) {
  const jobs = [
    warmJson('/api/public/groups', PREFETCH_KEYS.groups),
    warmJson('/api/public/teachers', PREFETCH_KEYS.teachers),
    warmBanners('schedule', PREFETCH_KEYS.bannersSchedule),
    warmBanners('profile', PREFETCH_KEYS.bannersProfile),
    warmJson('/api/public/theme', PREFETCH_KEYS.theme),
    fetch(apiUrl('/api/admin/maintenance/status'), { method: 'GET' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null),
    ...WARM_IMAGES.map((src) => loadImage(src, { timeoutMs: 2500 })),
  ];
  if (token) {
    jobs.push(
      axios
        .get(apiUrl('/users/me'), {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 3500,
        })
        .then((res) => {
          try {
            persistUser(res.data);
          } catch {
            /* ignore */
          }
          return res.data;
        })
        .catch(() => null)
    );
  }
  Promise.allSettled(jobs).catch(() => {});
}

/**
 * Короткий сплэш: только локальные картинки, без Google/jsDelivr/QR.
 * Данные и остальное греем в фоне, чтобы UI не ждал VPN.
 */
export async function preloadApp({ token, onProgress } = {}) {
  const report = (value) => {
    if (typeof onProgress === 'function') {
      onProgress(Math.max(0, Math.min(1, value)));
    }
  };

  report(0.12);
  const started = Date.now();
  await Promise.race([
    Promise.all(
      CRITICAL_IMAGES.map((src, i) =>
        loadImage(src, {
          timeoutMs: 1200,
          priority: i === 0 ? 'high' : 'auto',
        })
      )
    ),
    new Promise((resolve) => window.setTimeout(resolve, 900)),
  ]);
  report(0.72);

  warmInBackground(token);

  const wait = Math.max(0, 220 - (Date.now() - started));
  if (wait) await new Promise((r) => window.setTimeout(r, wait));
  report(1);
}
